import * as React from "react";
import {useEffect, useMemo, useState} from "react";
import {Avatar, Box, Card, CardActionArea, CircularProgress, Paper, Typography} from "@mui/material";
import {applicationType, userType} from "../../services/utils/types/types";
import Api from "../../services/server/api";
import {useSearchParams} from "react-router-dom";
import { getAgeByYearOfBirth } from "../../services/utils/helper";

type GroupedFamily = {
    mainApplicant: userType | null;
    spouse: userType | null;
    children: userType[];
    parents: userType[];
    others: userType[];
};

const getInitials = (name?: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
    return (first + last).toUpperCase();
};

const PhotoBubble: React.FC<{ person?: userType; size?: number; title?: string; highlight?: boolean; }> = ({ person, size = 96, title, highlight }) => {
    const src = person?.mainPhoto;
    const initials = getInitials(person?.name);
    const borderColor = highlight ? "#6C63FF" : "#e0e0e0";
    const ageStr = person?.yearOfBirth ? getAgeByYearOfBirth(person.yearOfBirth) : undefined;
    const showAge = ageStr && !isNaN(Number(ageStr));
    return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 1}}>
            <Card elevation={highlight ? 6 : 2} sx={{
                borderRadius: "50%",
                width: size,
                height: size,
                border: `3px solid ${borderColor}`,
                transition: "transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease",
                animation: "fadeInScale 500ms ease both",
                overflow: "hidden"
            }}>
                <CardActionArea sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "&:hover": { transform: "scale(1.04)" }
                }}>
                    {src ? (
                        <Avatar src={src} alt={person?.name} sx={{ width: "100%", height: "100%" }} />
                    ) : (
                        <Avatar sx={{ width: "100%", height: "100%", fontSize: size * 0.32, bgcolor: "#6C63FF" }}>
                            {initials || "?"}
                        </Avatar>
                    )}
                </CardActionArea>
            </Card>
            <Box sx={{ textAlign: "center", maxWidth: size * 1.9 }}>
                {!!title && (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {title}
                    </Typography>
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                    {person?.name ?? "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {showAge ? `Age ${ageStr}` : ''}
                </Typography>
            </Box>
        </Box>
    );
};

export const RelationshipChart: React.FC = () => {
    const [searchParams] = useSearchParams();
    const appId = searchParams.get("id");

    const [loading, setLoading] = useState<boolean>(true);
    const [application, setApplication] = useState<applicationType>({ person: [] });

    useEffect(() => {
        const api = new Api();
        setLoading(true);
        api.getApplication({ id: appId })
            .then((res) => {
                const app: applicationType = res?.data?.[0] ?? { person: [] };
                setApplication(app);
            })
            .finally(() => setLoading(false));
    }, [appId]);

    const grouped: GroupedFamily = useMemo(() => {
        const persons: userType[] = application.person ?? [];
        const main = persons.find(p => (p.relationship ?? "").toUpperCase() === "MAIN APPLICANT") ?? null;
        const spouse = persons.find(p => (p.relationship ?? "").toUpperCase() === "SPOUSE") ?? null;
        const children = persons.filter(p => {
            const r = (p.relationship ?? "").toUpperCase();
            return r === "SON" || r === "DAUGHTER";
        });
        const parents = persons.filter(p => {
            const r = (p.relationship ?? "").toUpperCase();
            return r === "FATHER" || r === "MOTHER";
        });
        const others = persons.filter(p => {
            const r = (p.relationship ?? "").toUpperCase();
            return ["MAIN APPLICANT", "SPOUSE", "SON", "DAUGHTER", "FATHER", "MOTHER"].indexOf(r) === -1;
        });
        return { mainApplicant: main, spouse, children, parents, others };
    }, [application]);

    return (
        <Box sx={{
            p: { xs: 2, sm: 3 },
            position: "relative",
            background: "linear-gradient(180deg, #fafbff 0%, #ffffff 60%)",
            minHeight: "100%"
        }}>
            <style>
                {`
                @keyframes fadeInScale {
                  from { opacity: 0; transform: scale(0.9); }
                  to { opacity: 1; transform: scale(1); }
                }
                .connector-vertical {
                  position: relative;
                }
                .connector-vertical::after {
                  content: "";
                  position: absolute;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 2px;
                  height: 28px;
                  background: #c7c7c7;
                }
                .connector-horizontal {
                  position: relative;
                }
                .connector-horizontal::before {
                  content: "";
                  position: absolute;
                  top: 50%;
                  left: 0;
                  right: 0;
                  height: 2px;
                  background: #c7c7c7;
                }
              `}
            </style>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                Family Relationship
            </Typography>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    {/* Parents Row */}
                    {grouped.parents.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Parents</Typography>
                            <Box className="connector-vertical" />
                            <Box className="connector-horizontal" sx={{
                                display: "flex",
                                gap: 3,
                                px: 3,
                                alignItems: "center",
                                "&::before": { display: grouped.parents.length > 1 ? "block" : "none" }
                            }}>
                                {grouped.parents.map((p) => (
                                    <PhotoBubble key={p.id ?? p.name} person={p} size={80} title={p.relationship ?? ""} />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Main + Spouse Row */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                        <PhotoBubble person={grouped.mainApplicant ?? undefined} size={120} title="MAIN APPLICANT" highlight />
                        {grouped.spouse && (
                            <>
                                <Box sx={{ width: 32, height: 2, bgcolor: "#c7c7c7" }} />
                                <PhotoBubble person={grouped.spouse} size={110} title="SPOUSE" />
                            </>
                        )}
                    </Box>

                    {/* Children Row */}
                    {grouped.children.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Children</Typography>
                            <Box className="connector-vertical" />
                            <Box className="connector-horizontal" sx={{
                                display: "flex",
                                gap: 3,
                                px: 3,
                                alignItems: "center",
                                "&::before": { display: grouped.children.length > 1 ? "block" : "none" }
                            }}>
                                {grouped.children.map((c) => (
                                    <PhotoBubble key={c.id ?? c.name} person={c} size={86} title={c.relationship ?? ""} />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Others (Siblings/Relatives) */}
                    {grouped.others.length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>Others</Typography>
                            <Box className="connector-horizontal" sx={{
                                display: "flex",
                                gap: 2,
                                px: 2,
                                alignItems: "center",
                                flexWrap: "wrap",
                                "&::before": { display: grouped.others.length > 1 ? "block" : "none" }
                            }}>
                                {grouped.others.map((o) => (
                                    <PhotoBubble key={o.id ?? o.name} person={o} size={72} title={o.relationship ?? ""} />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
                </Paper>
            )}
        </Box>
    );
};

export default RelationshipChart;


